import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { get } from 'lodash-es';
import { getStaticImageUrl } from 'client/utils/image-helpers';
import { EventToolbox } from 'client/utils/event-toolbox';
import { isUsed } from 'site-modules/shared/utils/inventory-utils/is-used';
import { TelephoneLink } from 'site-modules/shared/components/inventory/telephone-link/telephone-link';
import { DeliveryButton } from 'site-modules/shared/components/inventory/delivery-button/delivery-button';
import { DeliveryCopy } from 'client/site-modules/shared/components/inventory/delivery-copy/delivery-copy';
import { RETAILER_OFFERS, CARMAX, DRIVEWAY } from 'site-modules/shared/constants/inventory/buy-online-dealers';
import {
  getDealerLinkWithUTMParams,
  getCommonDeepLink,
} from 'client/site-modules/shared/utils/dealership-link-constructor';
import { ACTIONS, DATA_PATH } from 'client/engagement-handlers/inventory-engagement-handler/constants';
import { useContactDealer } from 'site-modules/shared/components/inventory/contact-dealer/contact-dealer';
import { RETAIL_CTA_TYPES } from 'site-modules/shared/components/inventory/contact-dealer/constants';

export function RetailerInfoPanel({
  showOffersList = false,
  showDeliveryButton = false,
  topCtaType = RETAIL_CTA_TYPES.LINK,
}) {
  const {
    vinData,
    formattedPhone,
    displayPrice,
    dealerInfo,
    visitorZipCode,
    ted6504Recipe,
    isProdEnv,
    isMobile,
    isForceDefault,
  } = useContactDealer();

  const parentDealershipName = get(vinData, DATA_PATH.PARENT_DEALERSHIP_NAME, '');

  const SHOP_RETAILER_OFFERS = {
    [CARMAX]: {
      ...RETAILER_OFFERS[CARMAX],
      copy: [
        { text: 'Stress-free online checkout' },
        { text: '10-day return period' },
        {
          text: (
            <DeliveryCopy
              price={displayPrice}
              vinData={vinData}
              textDelivery={RETAILER_OFFERS[CARMAX].copy[2].text.slice(0, -1)}
            />
          ),
        },
      ],
    },
    [DRIVEWAY]: {
      ...RETAILER_OFFERS[DRIVEWAY],
      copy: [
        { text: 'Stress-free online checkout' },
        { text: '7-day return period' },
        { text: <DeliveryCopy price={displayPrice} vinData={vinData} /> },
      ],
    },
  };

  const dealerData = SHOP_RETAILER_OFFERS[parentDealershipName];

  const url = getDealerLinkWithUTMParams({
    vinData,
    dealershipInfo: dealerInfo,
    zipCode: visitorZipCode,
    dealer: parentDealershipName,
    isProdEnv,
    isExtended: true,
    getDeepLink: getCommonDeepLink,
    ted6504Recipe,
    isForceDefault,
  });

  const { franchiseId, rooftopId } = get(vinData, 'dealerInfo', {});
  const city = get(vinData, 'dealerInfo.address.city', '');
  const stateCode = get(vinData, 'dealerInfo.address.stateCode', '');

  function handleBtnClick() {
    EventToolbox.fireCustomEvent(ACTIONS.PRICING_BOX_BTN_CLICK, {
      partnerId: get(vinData, 'dealerInfo.rooftopId'),
      value: get(dealerData, 'ctaTitle'),
      isMobile,
      type: isUsed(vinData.type) ? 'used' : 'new',
    });
  }

  return (
    <div>
      <div style={{ marginTop: '24px', marginBottom: '16px' }} className="d-flex justify-content-between">
        <div>
          <img
            className="d-inline-block my-auto mw-100 object-fit-contain"
            src={getStaticImageUrl(dealerData.logo)}
            alt={`${parentDealershipName} Logo`}
            loading="lazy"
            width="auto"
            height={dealerData.logoHeight}
          />
          <div className="size-14">
            {city}, {stateCode}
          </div>
        </div>

        {topCtaType === RETAIL_CTA_TYPES.PHONE ? (
          <TelephoneLink
            formattedPhone={formattedPhone}
            dealerInfo={{
              dataTrackingId: 'vdp_call_dealer',
              dataDealerFranchiseId: franchiseId,
              dataDealerRooftopId: rooftopId,
            }}
            showNumber
            iconClassName="text-primary-darker size-16"
            linkClassName="text-primary-darker text-decoration-none d-inline-block size-14"
            telephoneLinkCTAName="Call Dealer"
          />
        ) : (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-50 size-14"
            onClick={handleBtnClick}
            data-test="get_it_delivery_primary"
          >
            <span>
              {get(dealerData, 'ctaTitle')}
              <i className="icon-open_in_new medium ms-0_25" aria-hidden />
            </span>
          </a>
        )}
      </div>

      {showOffersList && (
        <ul className="list-unstyled mb-0">
          {dealerData.copy.map(({ text }, index) => (
            <li key={`offer_${index + 1}`} className={classNames('d-flex', { 'mb-0_25': index !== 2 })}>
              <i className="icon-checkmark text-primary me-0_5" aria-hidden />
              {text}
            </li>
          ))}
        </ul>
      )}

      {showDeliveryButton && (
        <DeliveryButton
          url={url}
          dataTest="get_it_delivery_primary"
          isMobile={isMobile}
          onClick={handleBtnClick}
          ctaTitle={get(dealerData, 'ctaTitle')}
          ctaClasses={get(dealerData, 'ctaClasses')}
        />
      )}
    </div>
  );
}

RetailerInfoPanel.propTypes = {
  showOffersList: PropTypes.bool,
  showDeliveryButton: PropTypes.bool,
  topCtaType: PropTypes.oneOf([RETAIL_CTA_TYPES.PHONE, RETAIL_CTA_TYPES.LINK]),
};
